import { useEffect, useMemo, useState } from "react";
import AppShell from "../shell/AppShell.jsx";
import DetailDrawer from "../components/DetailDrawer.jsx";
import { useBackofficeIdentity } from "../hierarchy/BackofficeIdentityContext.jsx";
import { useFakeLocationEvaluations, FAKE_LOCATION_EVALUATIONS_DEFAULT_PAGE_SIZE } from "./useFakeLocationEvaluations.js";
import { getFakeLocationEvaluationDetail, evaluateFakeLocation } from "./fakeLocationApi.js";
import "./fakeLocationReview.css";
import "../pages/states.css";
import Pagination from "../components/Pagination.jsx";

import PageSkeleton, { DetailSkeleton } from "../components/Skeleton.jsx";
const CRUMBS = ["Backoffice", "Tracking", "Fake Location Review"];
const DRAWER_TITLE_ID = "flr-drawer-title";

const RISK_LEVEL_OPTIONS = ["Low", "Medium", "High"];

const SUBTITLE_TEXT =
  "ตรวจสัญญาณตำแหน่งผิดปกติแบบอธิบายได้จากข้อมูลที่อุปกรณ์ส่งมา โดยยังไม่ใช้บริการภายนอกหรือปิดกั้นแรงงานอัตโนมัติ";

const POLICY_LINES = [
  "Scoring เป็น deterministic rule-based ไม่ใช่ AI/ML",
  "ใช้เฉพาะ WmxLocationEvents และ WmxWorkerLatestStatus ที่มีอยู่",
  "ต้องระบุ purpose และช่วงเวลาต้องถูกจำกัด",
  "High risk เท่านั้นที่สร้าง FakeLocationSuspected abnormal เมื่อ dryRun=false",
  "ระบบยังไม่แจ้งให้อัตโนมัติ ต้องติดต่อเอง",
];

const NOT_A_PUNISHMENT_WARNING = "ผลคะแนนนี้เป็นสัญญาณให้ตรวจสอบ ไม่ใช่การตัดสินลงโทษแรงงานอัตโนมัติ";
const DRYRUN_HINT = "dryRun=true จะไม่สร้าง abnormal";
const AUDIT_SAFE_NOTE = "Evaluation log ไม่เก็บรายการพิกัดทั้งหมด";
const SCOPE_WORKER_REQUIRED_MESSAGE = "กรุณาระบุรหัสแรงงาน (Worker) เพื่อดูรายการ Evaluation ในขอบเขตของคุณ";

const EMPTY_EVALUATE_FORM = { workerId: "", assignmentId: "", fromUtc: "", toUtc: "", dryRun: true, purpose: "" };

const RISK_BADGE_VARIANT = { Low: "success", Medium: "warning", High: "danger" };
const SEVERITY_BADGE_VARIANT = { Low: "info", Medium: "warning", High: "danger" };

function riskBadgeVariant(value) {
  return RISK_BADGE_VARIANT[value] ?? "neutral";
}

function severityBadgeVariant(value) {
  return SEVERITY_BADGE_VARIANT[value] ?? "neutral";
}

function formatDateTimeTh(value) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const datePart = date.toLocaleDateString("th-TH-u-ca-gregory", { day: "2-digit", month: "short", year: "numeric" });
  const timePart = date.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${datePart} · ${timePart} ICT`;
}

function describeListError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์เข้าถึงรายการ Evaluation นี้";
  }
  if (error?.status === 404) {
    return "ไม่พบแรงงานที่ระบุ";
  }
  return "กรุณาลองรีเฟรชหน้านี้อีกครั้ง หากยังพบปัญหากรุณาติดต่อฝ่ายปฏิบัติการ";
}

function describeDetailError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์ดูรายละเอียด Evaluation นี้";
  }
  if (error?.status === 404) {
    return "ไม่พบ Evaluation นี้ อาจถูกลบหรือย้ายแล้ว";
  }
  return "ไม่สามารถโหลดรายละเอียดได้ กรุณาลองใหม่อีกครั้ง";
}

function describeEvaluateError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์ประเมินแรงงานคนนี้";
  }
  if (error?.status === 404) {
    return "ไม่พบแรงงานที่ระบุ";
  }
  if (error?.status === 400) {
    return "กรุณาตรวจสอบข้อมูลที่กรอกอีกครั้ง";
  }
  return "ไม่สามารถประเมินได้ กรุณาลองใหม่อีกครั้ง";
}

/**
 * /backoffice/tracking/fake-location - the Backoffice Fake Location Review
 * screen (Prompt 121B, built from the W58 reference). Entirely over the
 * Prompt 120 Fake Location Signal API: a scoped, time-bounded evaluation
 * (POST .../fake-location/evaluate) that scores a worker's stored
 * dbo.WmxLocationEvents metadata with six deterministic, rule-based signals
 * (never AI/ML, never an external anti-fraud/geolocation service), a
 * paginated list of previously stored evaluations
 * (GET .../fake-location/evaluations), and a single-evaluation detail
 * (GET .../fake-location/evaluations/{evaluationId}).
 *
 * This screen never evaluates a geofence (canEvaluateGeofence is always
 * rendered false with the backend's own fixed reserved-reason string),
 * never touches face recognition or SOS, never sends a real notification -
 * notificationDeliveryStarted is always rendered false - and never implies
 * automatic worker punishment/blocking: every result and every High-risk
 * state carries the fixed warning that a score is a signal for human
 * review, never an automatic penalty. A FakeLocationSuspected abnormal is
 * only ever created by the backend when dryRun=false and riskLevel is High;
 * this page never exposes a notification-send action for one. No
 * coordinate/points list is ever requested, stored, or rendered anywhere on
 * this page - only aggregate reasons (reasonCode/severity/count/
 * description).
 */
export default function FakeLocationReviewPage() {
  const { data: identity } = useBackofficeIdentity();
  const primaryRole = identity?.primaryRole;

  const [workerIdFilter, setWorkerIdFilter] = useState("");
  const [riskLevelFilter, setRiskLevelFilter] = useState("");
  const [fromUtcFilter, setFromUtcFilter] = useState("");
  const [toUtcFilter, setToUtcFilter] = useState("");
  const [page, setPage] = useState(1);

  const [selectedEvaluationId, setSelectedEvaluationId] = useState(null);
  const [drawerView, setDrawerView] = useState(null); // null | "evaluate" | "detail"
  const [detailState, setDetailState] = useState({ status: "idle", data: null, error: null, forId: null });

  const [evaluateForm, setEvaluateForm] = useState(EMPTY_EVALUATE_FORM);
  const [evaluateValidationError, setEvaluateValidationError] = useState(null);
  const [evaluateSubmit, setEvaluateSubmit] = useState({ status: "idle", error: null, result: null });

  useEffect(() => {
    setPage(1);
  }, [workerIdFilter, riskLevelFilter, fromUtcFilter, toUtcFilter]);

  const filters = useMemo(
    () => ({
      page,
      pageSize: FAKE_LOCATION_EVALUATIONS_DEFAULT_PAGE_SIZE,
      workerId: workerIdFilter,
      riskLevel: riskLevelFilter,
      fromUtc: fromUtcFilter ? new Date(fromUtcFilter).toISOString() : "",
      toUtc: toUtcFilter ? new Date(toUtcFilter).toISOString() : "",
    }),
    [page, workerIdFilter, riskLevelFilter, fromUtcFilter, toUtcFilter]
  );

  const { status: listStatus, data: listData, error: listError, refetch: refetchList } = useFakeLocationEvaluations(filters);

  // Detail is fetched whenever selectedEvaluationId changes (row/card opened).
  useEffect(() => {
    if (!selectedEvaluationId) {
      setDetailState({ status: "idle", data: null, error: null, forId: null });
      return undefined;
    }

    let cancelled = false;
    setDetailState({ status: "loading", data: null, error: null, forId: selectedEvaluationId });

    getFakeLocationEvaluationDetail(selectedEvaluationId)
      .then((data) => {
        if (!cancelled) {
          setDetailState({ status: "ready", data, error: null, forId: selectedEvaluationId });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setDetailState({ status: "error", data: null, error, forId: selectedEvaluationId });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedEvaluationId]);

  function openDetail(evaluationId) {
    setSelectedEvaluationId(evaluationId);
    setDrawerView("detail");
  }

  function openEvaluate() {
    setEvaluateForm(EMPTY_EVALUATE_FORM);
    setEvaluateValidationError(null);
    setEvaluateSubmit({ status: "idle", error: null, result: null });
    setDrawerView("evaluate");
  }

  function closeDrawer() {
    setDrawerView(null);
    setSelectedEvaluationId(null);
  }

  function handleRefresh() {
    refetchList();
  }

  function handleEvaluateSubmit() {
    const trimmedWorkerId = evaluateForm.workerId.trim();
    const trimmedPurpose = evaluateForm.purpose.trim();

    if (!trimmedWorkerId) {
      setEvaluateValidationError("กรุณาระบุ workerId");
      return;
    }
    if (!evaluateForm.fromUtc || !evaluateForm.toUtc) {
      setEvaluateValidationError("กรุณาระบุ fromUtc และ toUtc");
      return;
    }
    if (!trimmedPurpose) {
      setEvaluateValidationError("กรุณาระบุ purpose");
      return;
    }

    setEvaluateValidationError(null);
    setEvaluateSubmit({ status: "submitting", error: null, result: null });

    evaluateFakeLocation({
      workerId: trimmedWorkerId,
      assignmentId: evaluateForm.assignmentId.trim() || undefined,
      fromUtc: new Date(evaluateForm.fromUtc).toISOString(),
      toUtc: new Date(evaluateForm.toUtc).toISOString(),
      dryRun: evaluateForm.dryRun,
      purpose: trimmedPurpose,
    })
      .then((result) => {
        setEvaluateSubmit({ status: "success", error: null, result });
        // A real (non-dryRun) evaluation may have just created an abnormal -
        // the list itself is worker-scoped and unaffected by score alone,
        // so only refetch when this evaluate call actually wrote one.
        if (result?.abnormalCreated) {
          refetchList();
        }
      })
      .catch((error) => {
        setEvaluateSubmit((previous) => ({ status: "error", error, result: previous.result }));
      });
  }

  const items = listData?.items ?? [];
  const totalCount = listData?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / FAKE_LOCATION_EVALUATIONS_DEFAULT_PAGE_SIZE));
  const hasActiveFilters = Boolean(workerIdFilter || riskLevelFilter || fromUtcFilter || toUtcFilter);

  const isInitialLoading = listStatus === "loading" && listData === null && listError === null;
  const scopeRequiresWorkerId = listStatus === "error" && listError?.status === 403 && !workerIdFilter;

  if (isInitialLoading) {
    return (
      <AppShell crumbs={CRUMBS}>
        <PageSkeleton variant="table" label="กำลังโหลดรายการ Evaluation..." />
      </AppShell>
    );
  }

  return (
    <AppShell crumbs={CRUMBS}>
      <div className="wx-flr-page">
        <header className="wx-flr-head">
          <div className="wx-flr-title">
            <h1>
              Fake Location Review <span className="wx-flr-title-th">ตรวจสัญญาณตำแหน่งผิดปกติ</span>
            </h1>
            <p>{SUBTITLE_TEXT}</p>
          </div>
          {primaryRole && (
            <span className="wx-flr-scope-pill">
              Scope <b>{primaryRole}</b>
            </span>
          )}
        </header>

        <div className="wx-flr-policy-banner" role="status">
          <div className="wx-flr-policy-banner-title">
            <span className="wx-flr-policy-dot" aria-hidden="true" />
            นโยบาย Fake Location Review
          </div>
          <ul>
            {POLICY_LINES.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>

        {/* A row of five KPI tiles stood here, every one of them rendering the
            word "สงวนไว้" over a pill reading "รอ Summary API" - a whole band of
            the page, on every visit, reporting that we had not built a summary
            endpoint. No number was ever shown. The list below is the real
            content; it is now the first thing under the header. */}

        <div className="wx-flr-toolbar">
          <div className="wx-flr-toolbar-actions">
            <button type="button" className="wx-button wx-button--ghost wx-flr-refresh-button" onClick={handleRefresh}>
              Refresh
            </button>
            <button type="button" className="wx-button wx-button--primary wx-flr-evaluate-button" onClick={openEvaluate}>
              + ประเมินสัญญาณตำแหน่ง
            </button>
          </div>

          <div className="wx-flr-filters">
            <label className="wx-flr-filter">
              <span>Worker</span>
              <input type="text" placeholder="รหัส (ไม่บังคับ)" value={workerIdFilter} onChange={(event) => setWorkerIdFilter(event.target.value)} />
            </label>
            <label className="wx-flr-filter">
              <span>Risk Level</span>
              <select value={riskLevelFilter} onChange={(event) => setRiskLevelFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {RISK_LEVEL_OPTIONS.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>
            <label className="wx-flr-filter">
              <span>From</span>
              <input type="date" value={fromUtcFilter} onChange={(event) => setFromUtcFilter(event.target.value)} />
            </label>
            <label className="wx-flr-filter">
              <span>To</span>
              <input type="date" value={toUtcFilter} onChange={(event) => setToUtcFilter(event.target.value)} />
            </label>
          </div>
        </div>

        <section className="wx-panel wx-flr-list-panel">
          <div className="wx-flr-list-head">
            <h2 className="wx-panel-title">รายการ Evaluation</h2>
            <span className="wx-flr-list-count">
              {totalCount} รายการ{listStatus === "loading" && listData !== null ? " · กำลังโหลด..." : ""}
            </span>
          </div>

          {scopeRequiresWorkerId ? (
            <p className="wx-empty-list">{SCOPE_WORKER_REQUIRED_MESSAGE}</p>
          ) : listStatus === "error" && listData === null ? (
            <p className="wx-flr-inline-error" role="alert">
              {describeListError(listError)}
            </p>
          ) : (
            <>
              {listStatus === "error" && listData !== null && (
                <p className="wx-flr-inline-error" role="alert">
                  {describeListError(listError)}
                </p>
              )}

              {items.length === 0 ? (
                <p className="wx-empty-list">{hasActiveFilters ? "ไม่พบรายการที่ตรงกับตัวกรอง" : "ยังไม่มี Evaluation ในขอบเขตที่มองเห็น"}</p>
              ) : (
                <>
                  <div className="wx-flr-table-wrap">
                    <table className="wx-flr-table">
                      <colgroup>
                    {/* 10 cols for 10 columns, summing to 100. The ten widths only reached 92%.
                        table-layout:fixed reads these in order and ignores
                        any extra, so a mismatched or short list sizes the
                        real columns off something that was never about
                        them. If a column is added or removed, change this
                        list too. */}
                    <col style={{ width: "11%" }} /><col style={{ width: "15%" }} /><col style={{ width: "8%" }} /><col style={{ width: "7%" }} />
                    <col style={{ width: "14%" }} /><col style={{ width: "8%" }} /><col style={{ width: "11%" }} /><col style={{ width: "11%" }} />
                    <col style={{ width: "9%" }} /><col style={{ width: "6%" }} />
                  </colgroup>
                      <thead>
                        <tr>
                          <th>Evaluation Code</th>
                          <th>แรงงาน</th>
                          <th>Risk</th>
                          <th>Score</th>
                          <th>ช่วงเวลา (UTC)</th>
                          <th>จุดที่ตรวจ</th>
                          <th>Abnormal</th>
                          <th>ประเมินเมื่อ (UTC)</th>
                          <th>ผู้ประเมิน</th>
                          <th aria-hidden="true">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item) => (
                          <tr key={item.evaluationId} className="wx-flr-row" onClick={() => openDetail(item.evaluationId)}>
                            <td>
                              <span className="wx-flr-code">{item.evaluationCode ?? "-"}</span>
                            </td>
                            <td>
                              <span className="wx-flr-cell-strong">{item.workerName ?? "-"}</span>
                              <span className="wx-flr-cell-sub">{item.workerCode ?? "-"}</span>
                            </td>
                            <td>
                              <span className={`wx-badge wx-badge--${riskBadgeVariant(item.riskLevel)}`}>{item.riskLevel}</span>
                            </td>
                            <td>
                              <strong>{item.score}</strong>
                            </td>
                            <td>
                              <span className="wx-flr-cell-strong">
                                {formatDateTimeTh(item.fromUtc) ?? "-"} - {formatDateTimeTh(item.toUtc) ?? "-"}
                              </span>
                            </td>
                            <td>{item.pointCount}</td>
                            <td>{item.abnormalEventId ? <span className="wx-flr-code">{item.abnormalEventId}</span> : <span className="wx-flr-reserved-text">-</span>}</td>
                            <td>
                              <span className="wx-flr-cell-strong">{formatDateTimeTh(item.evaluatedAtUtc) ?? "-"}</span>
                            </td>
                            <td>
                              <span className="wx-flr-cell-strong">{item.evaluatedByUserId ?? "-"}</span>
                            </td>
                            <td>
                              <button
                                type="button"
                                className="wx-flr-row-action"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openDetail(item.evaluationId);
                                }}
                              >
                                เปิดรายละเอียด
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="wx-flr-cards">
                    {items.map((item) => (
                      <EvaluationCard key={item.evaluationId} item={item} onOpen={() => openDetail(item.evaluationId)} />
                    ))}
                  </div>

              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
                </>
              )}
            </>
          )}
        </section>

        <p className="wx-flr-footnote">หน้านี้ไม่แสดงพิกัดรายจุดของแรงงาน</p>
      </div>

      <DetailDrawer
        open={drawerView !== null}
        onClose={closeDrawer}
        labelledBy={DRAWER_TITLE_ID}
        selectableSelector="tr.wx-flr-row, .wx-flr-card, .wx-flr-refresh-button, .wx-flr-evaluate-button"
      >
        {drawerView === "evaluate" && (
          <EvaluateView
            titleId={DRAWER_TITLE_ID}
            form={evaluateForm}
            onChange={(patch) => setEvaluateForm((previous) => ({ ...previous, ...patch }))}
            validationError={evaluateValidationError}
            submitState={evaluateSubmit}
            onSubmit={handleEvaluateSubmit}
          />
        )}
        {drawerView === "detail" && <EvaluationDetailView titleId={DRAWER_TITLE_ID} state={detailState} />}
      </DetailDrawer>
    </AppShell>
  );
}

function EvaluationCard({ item, onOpen }) {
  const abnormalCreated = Boolean(item.abnormalEventId);
  return (
    <div className="wx-flr-card" onClick={onOpen}>
      <div className="wx-flr-card-top">
        <div>
          <strong>{item.evaluationCode ?? "-"}</strong>
          <span className="wx-flr-card-sub">
            {item.workerName ?? "-"} · {item.workerCode ?? "-"}
          </span>
        </div>
        <div className="wx-flr-card-tags">
          <span className={`wx-badge wx-badge--${riskBadgeVariant(item.riskLevel)}`}>{item.riskLevel}</span>
        </div>
      </div>
      <div className="wx-flr-card-grid">
        <div>
          <label>Score</label>
          <strong>{item.score}</strong>
        </div>
        <div>
          <label>จุดที่ตรวจ</label>
          <strong>{item.pointCount}</strong>
        </div>
        <div>
          <label>Abnormal สร้างแล้ว</label>
          <strong>{abnormalCreated ? `ใช่ (${item.abnormalEventId})` : "ไม่"}</strong>
        </div>
        <div>
          <label>ประเมินเมื่อ</label>
          <strong>{formatDateTimeTh(item.evaluatedAtUtc) ?? "-"}</strong>
        </div>
      </div>
      <button
        type="button"
        className="wx-flr-row-action wx-flr-row-action--full"
        onClick={(event) => {
          event.stopPropagation();
          onOpen();
        }}
      >
        เปิดรายละเอียด
      </button>
    </div>
  );
}

function ReasonsList({ reasons }) {
  if (!reasons || reasons.length === 0) {
    return <p className="wx-flr-note-muted wx-flr-note-muted--left">ไม่พบสัญญาณผิดปกติในช่วงเวลานี้</p>;
  }
  return (
    <div className="wx-flr-reason-list">
      {reasons.map((reason) => (
        <div className="wx-flr-reason-row" key={reason.reasonCode}>
          <div className="wx-flr-reason-row-top">
            <strong>{reason.reasonCode}</strong>
            <span className={`wx-badge wx-badge--${severityBadgeVariant(reason.severity)}`}>{reason.severity}</span>
          </div>
          <p>{reason.description}</p>
          <span className="wx-flr-reason-count">count: {reason.count}</span>
        </div>
      ))}
    </div>
  );
}

function EvaluateView({ titleId, form, onChange, validationError, submitState, onSubmit }) {
  const submitting = submitState.status === "submitting";
  const result = submitState.result;
  const isHigh = result?.riskLevel === "High";

  return (
    <div className="wx-flr-drawer">
      <span className="wx-flr-drawer-kicker">EVALUATE</span>
      <h2 id={titleId} className="wx-flr-drawer-title">
        ประเมินสัญญาณตำแหน่งผิดปกติ
      </h2>
      <p className="wx-flr-drawer-sub">ตรวจหาการปลอมพิกัดของแรงงานในช่วงเวลาที่เลือก</p>

      <section className="wx-flr-dsec">
        <h3>พารามิเตอร์</h3>
        <label className="wx-flr-field">
          <span>
            รหัสแรงงาน <b>*</b>
          </span>
          <input type="text" aria-label="รหัสแรงงาน" placeholder="เช่น wrk_7x2k9qf3ab" value={form.workerId} onChange={(event) => onChange({ workerId: event.target.value })} />
        </label>
        <label className="wx-flr-field">
          <span>
            assignmentId <span className="wx-flr-optional">(ไม่บังคับ)</span>
          </span>
          <input type="text" aria-label="รหัสงาน" placeholder="เช่น asg_7x2k9qf3ab" value={form.assignmentId} onChange={(event) => onChange({ assignmentId: event.target.value })} />
        </label>
        <div className="wx-flr-field-row">
          <label className="wx-flr-field">
            <span>
              ตั้งแต่ <b>*</b>
            </span>
            <input type="datetime-local" aria-label="ตั้งแต่" value={form.fromUtc} onChange={(event) => onChange({ fromUtc: event.target.value })} />
          </label>
          <label className="wx-flr-field">
            <span>
              ถึง <b>*</b>
            </span>
            <input type="datetime-local" aria-label="ถึง" value={form.toUtc} onChange={(event) => onChange({ toUtc: event.target.value })} />
          </label>
        </div>
        <label className="wx-flr-field wx-flr-toggle-field">
          <span>โหมดทดลอง</span>
          <span className="wx-flr-toggle">
            <input type="checkbox" aria-label="โหมดทดลอง" checked={form.dryRun} onChange={(event) => onChange({ dryRun: event.target.checked })} />
            <span className="wx-flr-toggle-track">
              <span className="wx-flr-toggle-thumb" />
            </span>
          </span>
        </label>
        <span className="wx-flr-field-hint">{DRYRUN_HINT}</span>
        <label className="wx-flr-field">
          <span>
            purpose <b>*</b>
          </span>
          <textarea aria-label="purpose" placeholder="เช่น ตรวจสอบตามคำร้องเรียนเรื่องพิกัดผิดปกติ" value={form.purpose} onChange={(event) => onChange({ purpose: event.target.value })} />
        </label>
        <span className="wx-flr-field-hint">ต้องระบุ purpose ก่อนประเมิน · ช่วงเวลาไม่เกิน 7 วัน · maxPoints ถูกจำกัดสูงสุด 2000 จุดเสมอ</span>

        {validationError && (
          <p className="wx-flr-form-message" role="alert">
            {validationError}
          </p>
        )}
        {submitState.status === "error" && (
          <p className="wx-flr-form-message" role="alert">
            {describeEvaluateError(submitState.error)}
          </p>
        )}

        <button type="button" className="wx-button wx-button--primary wx-flr-run-evaluate-button" disabled={submitting} onClick={onSubmit}>
          {submitting ? "กำลังประเมิน..." : "เริ่มการประเมิน"}
        </button>
      </section>

      {result && (
        <>
          <section className={`wx-flr-dsec${isHigh ? " wx-flr-dsec--high" : ""}`}>
            <h3>ผลลัพธ์การประเมิน</h3>
            <div className="wx-flr-kv-row">
              <span>รหัสแรงงาน</span>
              <strong>{result.workerCode ?? "-"}</strong>
            </div>
            <div className="wx-flr-kv-row">
              <span>รหัสงาน</span>
              <strong>{result.assignmentId ?? "-"}</strong>
            </div>
            <div className="wx-flr-kv-row">
              <span>fromUtc - toUtc</span>
              <strong>
                {formatDateTimeTh(result.fromUtc) ?? "-"} - {formatDateTimeTh(result.toUtc) ?? "-"}
              </strong>
            </div>
            <div className="wx-flr-kv-row">
              <span>โหมดทดลอง</span>
              <strong>{String(result.dryRun)}</strong>
            </div>
            <div className="wx-flr-kv-row">
              <span>จำนวนจุด</span>
              <strong>{result.pointCount}</strong>
            </div>
            <div className="wx-flr-kv-row">
              <span>score</span>
              <strong>{result.score}</strong>
            </div>
            <div className="wx-flr-kv-row">
              <span>ระดับความเสี่ยง</span>
              <strong>
                <span className={`wx-badge wx-badge--${riskBadgeVariant(result.riskLevel)}`}>{result.riskLevel}</span>
              </strong>
            </div>
            <div className="wx-flr-kv-row">
              <span>สร้างรายการผิดปกติ</span>
              <strong>{String(result.abnormalCreated)}</strong>
            </div>
            {result.abnormalEventId && (
              <div className="wx-flr-kv-row">
                <span>รหัสรายการผิดปกติ</span>
                <strong className="wx-flr-code">{result.abnormalEventId}</strong>
              </div>
            )}
            <div className="wx-flr-kv-row">
              <span>ข้าม (มีรายการค้างอยู่แล้ว)</span>
              <strong>{String(result.skippedOpenDuplicate)}</strong>
            </div>

            <div className="wx-flr-notice">ระบบยังไม่แจ้งให้อัตโนมัติ กรุณาติดต่อแรงงานหรือหัวหน้างานเอง</div>
          </section>

          <section className="wx-flr-dsec">
            <h3>เหตุผลของคะแนน (reasons)</h3>
            <ReasonsList reasons={result.reasons} />
            <p className="wx-flr-note-muted wx-flr-note-muted--left">ไม่มีรายการพิกัด (latitude/longitude) ปรากฏในผลลัพธ์นี้ — reasons เป็นค่าสรุปเท่านั้น</p>
          </section>

          <div className={`wx-flr-notice${isHigh ? " wx-flr-notice--highlight" : ""}`}>{NOT_A_PUNISHMENT_WARNING}</div>
        </>
      )}
    </div>
  );
}

function EvaluationDetailView({ titleId, state }) {
  if (state.status === "loading" || state.status === "idle") {
    return (
      <DetailSkeleton label="กำลังโหลดรายละเอียด..." />
    );
  }

  if (state.status === "error") {
    return (
      <div className="wx-flr-drawer-state" role="alert">
        <h2 id={titleId} className="wx-state-title">
          ไม่สามารถโหลดรายละเอียดได้
        </h2>
        <p className="wx-state-body">{describeDetailError(state.error)}</p>
      </div>
    );
  }

  const detail = state.data;
  const isHigh = detail.riskLevel === "High";

  return (
    <div className="wx-flr-drawer">
      <span className="wx-flr-drawer-kicker">EVALUATION · DETAIL</span>
      <h2 id={titleId} className="wx-flr-drawer-title">
        รายละเอียด Evaluation
      </h2>
      <p className="wx-flr-drawer-sub">
        {detail.evaluationCode ?? "-"} · {detail.workerName ?? "-"}
      </p>
      <div className="wx-flr-drawer-status">
        <span className={`wx-badge wx-badge--${riskBadgeVariant(detail.riskLevel)}`}>{detail.riskLevel}</span>
        <span className="wx-badge wx-badge--neutral">score {detail.score}</span>
      </div>

      <section className={`wx-flr-dsec${isHigh ? " wx-flr-dsec--high" : ""}`}>
        <h3>ข้อมูล Evaluation</h3>
        <div className="wx-flr-kv-row">
          <span>รหัสการตรวจ</span>
          <strong className="wx-flr-code">{detail.evaluationCode ?? "-"}</strong>
        </div>
        <div className="wx-flr-kv-row">
          <span>workerCode / workerName</span>
          <strong>
            {detail.workerCode ?? "-"} · {detail.workerName ?? "-"}
          </strong>
        </div>
        <div className="wx-flr-kv-row">
          <span>รหัสงาน</span>
          <strong>{detail.assignmentId ?? "-"}</strong>
        </div>
        <div className="wx-flr-kv-row">
          <span>fromUtc - toUtc</span>
          <strong>
            {formatDateTimeTh(detail.fromUtc) ?? "-"} - {formatDateTimeTh(detail.toUtc) ?? "-"}
          </strong>
        </div>
        <div className="wx-flr-kv-row">
          <span>purpose</span>
          <strong>{detail.purpose ?? "-"}</strong>
        </div>
        <div className="wx-flr-kv-row">
          <span>จำนวนจุด</span>
          <strong>{detail.pointCount}</strong>
        </div>
        <div className="wx-flr-kv-row">
          <span>ตรวจโดย</span>
          <strong>{detail.evaluatedByUserId ?? "-"}</strong>
        </div>
        <div className="wx-flr-kv-row">
          <span>ตรวจเมื่อ</span>
          <strong>{formatDateTimeTh(detail.evaluatedAtUtc) ?? "-"}</strong>
        </div>
      </section>

      <section className="wx-flr-dsec">
        <h3>เหตุผลของคะแนน (reasons)</h3>
        <ReasonsList reasons={detail.reasons} />
      </section>

      <section className="wx-flr-dsec">
        <h3>อ้างอิง Abnormal Event</h3>
        {detail.abnormalEventId ? (
          <div className="wx-flr-abnormal-ref">
            <span className="wx-badge wx-badge--danger">FakeLocationSuspected</span>
            <span className="wx-flr-code">{detail.abnormalEventId}</span>
          </div>
        ) : (
          <p className="wx-flr-note-muted wx-flr-note-muted--left">ไม่มี Abnormal Event สำหรับ Evaluation นี้</p>
        )}
      </section>

      <div className="wx-flr-notice">{AUDIT_SAFE_NOTE}</div>
    </div>
  );
}
